import { useState } from 'react';
import { CourseClient, CourseDTO, ApiResponse_string, UpdateCourseStatusDTO, ForfeitureRequestDTO } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

export const useCourseApi = () => {
    const apiClient = new CourseClient(API_BASE_URL, axiosInstance);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const create = async (data: CourseDTO) => {
        setIsLoading(true);
        try {
            await apiClient.createCourse(data);
        } catch (err: any) {
            setError(err.message || 'Error creating Course');
        } finally {
            setIsLoading(false);
        }
    };

    const edit = async (id: string, data: CourseDTO) => {
        setIsLoading(true);
        try {
            await apiClient.updateCourse(id, data);
        } catch (err: any) {
            setError(err.message || 'Error updating Course');
        } finally {
            setIsLoading(false);
        }
    };

    const getById = async (id: string) => {
        setIsLoading(true);
        try {
            return await apiClient.getCourseById(id);
        } catch (err: any) {
            setError(err.message || 'Error fetching Course');
        } finally {
            setIsLoading(false);
        }
    };

    const search = async (filters: any) => {
        setIsLoading(true);
        try {
            return await apiClient.searchCourses(filters);
        } catch (err: any) {
            setError(err.message || 'Error searching Courses');
        } finally {
            setIsLoading(false);
        }
    };

    const remove = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.deleteCourse(id);
        } catch (err: any) {
            setError(err.message || 'Error deleting Course');
        } finally {
            setIsLoading(false);
        }
    };

    const extract = async (
        filters: any
    ): Promise<ApiResponse_string> => {
        setIsLoading(true);
        try {
            return await apiClient.exportCourses(filters);
        } catch (err: unknown) {
            handleHookError(err, 'Error exporting Courses', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateProofOfPayment = async (id: string, fileRecordId: string) => {
        setIsLoading(true);
        try {
            await apiClient.updateCourseProofOfPayment(id, fileRecordId);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const sendProofOfPaymentLink = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.sendCourseProofOfPaymentLink(id);
        } catch (err: any) {
            setError(err.message || 'Error updating proof of payment');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const restoreFromDeleted = async (id: string) => {
        setIsLoading(true);
        try {
            await apiClient.restoreFromDeletedCourse(id);
        } catch (err: any) {
            setError(err.message || 'Error restoring Course from deleted state');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateStatus = async (data: UpdateCourseStatusDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.updateCourseQuickStatus(data);
        } catch (err: any) {
            setError(err.message || 'Error updating course statuses');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getFeedback = async (id: string): Promise<string | undefined> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getCourseFeedback(id);
            return response.data ?? undefined;
        } catch (err: any) {
            setError(err.message || 'Error fetching Course feedback');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getStudyMaterial = async (courseId: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await apiClient.getCourseStudyMaterial(courseId);
            if (response.success && response.data) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to get study material URL');
        } catch (err: any) {
            handleHookError(err, 'Error getting study material', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const forfeit = async (id: string, data: ForfeitureRequestDTO) => {
        setIsLoading(true);
        try {
            return await apiClient.forfeitCourse(id, data);
        } catch (err: any) {
            handleHookError(err, 'Error forfeiting course', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const getForfeitureDocument = async (id: string): Promise<string> => {
        setIsLoading(true);
        try {
            const response = await (apiClient as any).getCourseForfeitureDocument(id);
            if (response.success && response.data) {
                return response.data;
            }
            throw new Error(response.message || 'Failed to get forfeiture document URL');
        } catch (err: any) {
            handleHookError(err, 'Error getting forfeiture document', setError);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return { create, edit, getById, search, extract, remove, restoreFromDeleted, updateProofOfPayment, sendProofOfPaymentLink, updateStatus, getStudyMaterial, getFeedback, forfeit, getForfeitureDocument, isLoading, error };
};